
import { OrganizationType, AssessorRole } from "@/types/assessment";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect } from "react";

interface AssessorDetailsSectionProps {
  formState: {
    assessorName: string;
    assessorRole: AssessorRole;
    assessorEmail: string;
  };
  setFormState: (state: any) => void;
}

const AssessorDetailsSection = ({
  formState,
  setFormState,
}: AssessorDetailsSectionProps) => {
  const { user } = useAuth();
  const isLoggedIn = !!user;

  // Update assessor details when user data is available
  useEffect(() => {
    if (user) {
      setFormState((prev: any) => ({
        ...prev,
        assessorName: user.fullName || prev.assessorName,
        assessorEmail: user.email || prev.assessorEmail
      }));
    }
  }, [user, setFormState]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormState({
      ...formState,
      [e.target.name]: e.target.value,
    });
  };

  const handleSelectChange = (name: string, value: AssessorRole) => {
    setFormState({
      ...formState,
      [name]: value,
    });
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-gray-900">Assessor Details</h2>

      <div>
        <Label htmlFor="assessorName">Assessor Name</Label>
        <Input
          type="text"
          id="assessorName"
          name="assessorName"
          value={formState.assessorName}
          onChange={handleInputChange}
          required
          readOnly={isLoggedIn}
          className={isLoggedIn ? "bg-gray-100" : ""}
        />
        {isLoggedIn && (
          <p className="text-sm text-gray-500 mt-1">
            Using your logged-in name
          </p>
        )}
      </div>

      <div>
        <Label htmlFor="assessorRole">Assessor Role *</Label>
        <Select 
          value={formState.assessorRole} 
          onValueChange={(value) => handleSelectChange("assessorRole", value as AssessorRole)}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select assessor role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Board Member / Chairman">Board Member / Chairman</SelectItem>
            <SelectItem value="CEO / Managing Director">CEO / Managing Director</SelectItem>
            <SelectItem value="Technology Executive (CIO/CTO/CDO)">Technology Executive (CIO/CTO/CDO)</SelectItem>
            <SelectItem value="Other C-Suite Executive">Other C-Suite Executive</SelectItem>
            <SelectItem value="Senior Management / VP">Senior Management / VP</SelectItem>
            <SelectItem value="Middle Management / Director">Middle Management / Director</SelectItem>
            <SelectItem value="Technical Lead / Architect">Technical Lead / Architect</SelectItem>
            <SelectItem value="Business Unit Head">Business Unit Head</SelectItem>
            <SelectItem value="Project Manager">Project Manager</SelectItem>
            <SelectItem value="External Consultant">External Consultant</SelectItem>
            <SelectItem value="Other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="assessorEmail">Assessor Email</Label>
        <Input
          type="email"
          id="assessorEmail"
          name="assessorEmail"
          value={formState.assessorEmail}
          onChange={handleInputChange}
          required
          readOnly={isLoggedIn}
          className={isLoggedIn ? "bg-gray-100" : ""}
        />
        {isLoggedIn && (
          <p className="text-sm text-gray-500 mt-1">
            Using your logged-in email address
          </p>
        )}
      </div>
    </div>
  );
};

export default AssessorDetailsSection;
